'use client';

import { motion } from "framer-motion";
import { PageHero } from "@/components/shared/PageHero";
import { useState } from "react";
import ContactModal from "@/components/ContactModal";

const services = [
  {
    title: "Web Design & Development",
    description: "Professional web solutions that drive results and enhance your online presence.",
    features: [
      "Website Design: Starting at $8,000",
      "Landing Page Design: Starting at $4,000",
      "Responsive Design: Starting at $3,500",
      "Frontend Development: Starting at $6,000",
      "Website Maintenance: From $800/month",
      "Website Audits: Starting at $1,500"
    ]
  },
  {
    title: "Commercial Photography",
    description: "Professional photography that showcases your products and brand in the best light.",
    features: [
      "Product Photography: Starting at $1,500/session",
      "Corporate Headshots: Starting at $800/session",
      "Lifestyle Photography: Starting at $2,000/session",
      "Event Coverage: From $600/hour"
    ]
  },
  {
    title: "Video Production",
    description: "High-quality video content that tells your brand's story and engages your audience.",
    features: [
      "Brand Videos: Starting at $8,000",
      "Product Videos: Starting at $3,000",
      "Social Media Content: From $1,500/video",
      "Event Coverage: Starting at $1,000/hour"
    ]
  },
  {
    title: "Visual Design",
    description: "Stunning visual assets that capture your brand's essence and engage your audience.",
    features: [
      "Graphic Design: From $200/hour",
      "Logo Design: Starting at $1,500",
      "Brand Identity Creation: Starting at $7,500",
      "Visual Content Creation: From $250/item"
    ]
  },
  {
    title: "Post-Production",
    description: "Expert editing and enhancement to make your visual content stand out.",
    features: [
      "Photo Editing: Starting at $25/image",
      "Video Editing: Starting at $1,500/project",
      "Color Grading: Starting at $1,200/project"
    ]
  },
  {
    title: "Digital Products",
    description: "Innovative digital solutions that solve problems and create value for your users.",
    features: [
      "Mobile App Development: Starting at $30,000",
      "UI/UX Design: Starting at $8,000",
      "Digital Product Design: Starting at $12,000",
      "Wireframing: Starting at $2,500"
    ]
  },
  {
    title: "AI Solutions",
    description: "Cutting-edge AI implementations that streamline operations and enhance customer experience.",
    features: [
      "AI Agent/Receptionist: Starting at $8,000 setup + $600/month"
    ]
  },
  {
    title: "Content Strategy",
    description: "Strategic planning and execution of visual content that drives engagement.",
    features: [
      "Content Planning: Starting at $2,500",
      "Shoot Direction: Starting at $1,500/day",
      "Content Calendar: Starting at $800/month"
    ]
  },
  {
    title: "Customer Experience",
    description: "Comprehensive support solutions that ensure exceptional customer satisfaction.",
    features: [
      "Starter Support Package: From $1,500/month",
      "Premium Support Package: From $2,500/month",
      "Customer Support Leadership Lab: One-time $5,000"
    ]
  }
];


export function ServicesContent() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const filteredServices = services;

  return (
    <main className="min-h-screen bg-white">
      <PageHero
        title="What We Do"
        description="Transform your brand's presence with design, development, and strategy that drives real results."
      />


      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => {
                  setSelectedService(service.title);
                  setIsContactOpen(true);
                }}
                className="bg-white rounded-3xl p-10 border border-[#171A20]/5 hover:border-[#171A20]/10 transition-all duration-300 group relative overflow-hidden cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-[#171A20]/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative">
                  <div className="mb-12">
                    <h3 className="text-2xl font-bold mb-4 group-hover:opacity-90 transition-opacity duration-300" style={{ color: 'rgb(29,29,31)' }}>
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-lg leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                      {service.description}
                    </p>
                  </div>
                  <div className="space-y-8">
                    {service.features.map((feature, featureIndex) => {
                      const [serviceName] = feature.split(": ");
                      return (
                        <div
                          key={featureIndex}
                          className="flex items-center gap-4"
                        >
                          <svg
                            className="w-5 h-5 text-[#171A20]/80 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-gray-700 text-lg">
                            {serviceName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 relative" style={{ backgroundColor: 'rgb(245,245,247)' }}>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundSize: '40px 40px',
            backgroundPosition: '0 0, 20px 20px'
          }}
        />
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10 relative">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8 uppercase" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', letterSpacing: '0.025em' }}>
              Our Process
            </h2>
            <p className="text-xl md:text-2xl" style={{ color: 'rgba(0,0,0,0.88)' }}>
              We follow a proven process to ensure every project delivers exceptional results.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {[
              {
                step: "01",
                title: "Discovery",
                description: "We dive deep to understand your brand, goals, and challenges."
              },
              {
                step: "02",
                title: "Strategy",
                description: "We develop a tailored plan to achieve your objectives."
              },
              {
                step: "03",
                title: "Creation",
                description: "We bring your vision to life with expert execution."
              },
              {
                step: "04",
                title: "Optimization",
                description: "We measure, refine, and optimize for ongoing success."
              }
            ].map((phase, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="text-6xl md:text-7xl font-bold mb-8 group-hover:scale-110 transition-transform duration-300" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif' }}>
                  {phase.step}
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: 'rgb(29,29,31)' }}>{phase.title}</h3>
                <p className="text-lg leading-relaxed" style={{ color: 'rgba(0,0,0,0.7)' }}>{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ContactModal
        open={isContactOpen}
        onClose={() => {
          setIsContactOpen(false);
          setSelectedService(null);
        }}
        initialService={selectedService}
      >
        <div className="hidden" />
      </ContactModal>
    </main>
  );
} 